<script setup lang="ts">
import authV1BottomShape from '@/assets/images/svg/auth-v1-bottom-shape.svg'
import authV1TopShape from '@/assets/images/svg/auth-v1-top-shape.svg'
import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import { auth } from '@/firebase'
import { useUserStore } from '@/stores/user'
import {
  computed,
  onBeforeMount,
  onMounted,
  reactive,
  ref,
  watchEffect,
} from 'vue'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import {
  emailValidator,
  numberValidator,
  requiredValidator,
  strongPasswordValidator,
} from '@validators'
import {
  createUser,
  userRecaptchaVerify,
  verifyUserByEmail,
} from '@/services/userService'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useRoute, useRouter } from 'vue-router'
import { capitalizeString } from '@/utils/stringHelpers'
import { useRootStore } from '@/stores/root'
import { transformToUserProfileModel } from '@/utils/objectTransformers/user'
import validator from 'validator'
import { ElementIDs } from '@/common/enums/elementIdsEnum'
import {
  getWhiteLabelPortalLogo,
  getWhiteLabelPortalLogoSize,
  getWhiteLabelPortalName,
  getWhiteLabelSignupHeading,
  getWhiteLabelSignupSubHeading,
  isBionicSite,
} from '@/utils/whiteLabelHelpers'
import dashboardV2 from '@/assets/images/svg/dashboardV2.svg'
import googleIcon from '@/assets/images/icons/v2-design/google-icon.svg'
import ratingColored from '@/assets/images/icons/v2-design/rating-colored.svg'
import g2g from '@/assets/images/icons/v2-design/g2g.svg'
import Rating from '@/components/Rating.vue'

const captchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
const store = useUserStore()
const rootStore = useRootStore()

const defUserValue = {
  name: '',
  email: '',
  password: '',
  privacyPolicies: false,
  registeringAsCollaborator: false,
  signupSource: null,
  noOfSites: 0,
}

const form = ref(defUserValue)
const route = useRoute()
const router = useRouter()

const refVForm = ref()
const isPasswordVisible = ref(false)
const showPolicyError = ref(false)
const loading = ref(false)

const userType = ref('')
const affiliateToken = ref('')

const recaptcha = ref(null)
const captchaError = ref('')

watchEffect(() => {
  if (!window.grecaptcha) {
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }
})
// recaptcha
const doRecaptchaCheck = async () => {
  try {
    captchaError.value = ''

    const rcToken = window.grecaptcha?.getResponse()
    if (!rcToken) {
      captchaError.value = 'Please complete the CAPTCHA.'
      throw new Error('Please complete the CAPTCHA.')
    }
    await userRecaptchaVerify(rcToken).catch((e) => {
      captchaError.value = 'Captcha verification failed. Please try again.'
      throw e
    })
    return rcToken
  } catch (e: any) {
    throw e
  }
}

const isBionicPlatform = computed(() => {
  return isBionicSite()
})
const registerWithEmail = async () => {
  try {
    loading.value = true
    if (isBionicPlatform.value) await doRecaptchaCheck()

    const splitedNames = form.value?.name
      ?.trim()
      ?.split(' ')
      ?.map((str: string) => str.trim())
    const firstName = splitedNames?.[0]?.trim()
    const lastName = splitedNames
      ?.map((nm) => nm.trim())
      ?.join(' ')
      .replace(firstName, '')
    createUserWithEmailAndPassword(auth, form.value.email, form.value?.password)
      .then(async (r) => {
        const firebaseUser = r.user
        try {
          console.log('Attempting to  creating user in database')
          const savedUser = await createUser({
            uuid: firebaseUser.uid,
            email: form.value?.email,
            firstName: firstName,
            lastName: lastName,
            signupSource: form.value?.signupSource,
            affiliateToken: affiliateToken.value,
            noOfSites: form.value.noOfSites,
          })
          store.setUserProfile(transformToUserProfileModel(savedUser))
          // router.replace(route.query.to ? String(route.query.to) : '/')
          localStorage.setItem(`is-new-${savedUser.email}`, String(true))
          // Only send email verification if the user is a white-label customer
          if (savedUser.isWlCustomer) {
            // Send email verification for white-label customers
            await verifyUserByEmail(firebaseUser.email)
              .then(async () => {
                await auth.signOut()
                await router.replace('/login')
                rootStore.showSnackbar(
                  'Verification email sent. Please verify your email and login',
                  'success',
                )
              })
              .catch(() => {
                throw new Error('Error sending verification email')
              })
          } else {
            // Skip email verification for non-white-label customers / Manual verification
            await auth.signOut()
            router.replace('/login')
            rootStore.showSnackbar('Registration successful!', 'success')
          }
          try {
            // Fire Meta Pixel Lead Event
            // @ts-ignore
            if (window.fbq) {
              // @ts-ignore
              window.fbq('track', 'Lead', {
                content_name: 'Register',
                content_category: 'Registration',
                content_type: 'Iframe-Signup',
                content_ids: [savedUser.id],
                email: savedUser.email,
              })
            } else {
              console.log('fbq not found')
            }
          } catch (e: any) {
            console.log('Error sending fbq event', e?.message)
            // do nothing
          }
        } catch (e) {
          await r.user.delete()
          rootStore.showSnackbar('Failed to register user', 'error')
        }
      })
      .catch((error: any) => {
        if (store.userProfile) {
          store.logout()
        } else if (auth.currentUser) {
          auth.signOut()
        }
        rootStore.showSnackbar(
          `Failed to login. ${
            error.code
              ? error.code
                  .split('/')
                  .pop()
                  .split('-')
                  .map((str: string) => capitalizeString(str))
                  .join(' ')
              : error.message
          }`,
          'error',
        )
      })
      .finally(() => {
        loading.value = false
      })
  } catch (e: any) {
    loading.value = false
    console.log('Error registering user', e)
    rootStore.handleAPIException(e, 'Failed to register user')
  }
}
const onSubmit = () => {
  showPolicyError.value = false
  refVForm.value?.validate().then(({ valid: isValid }: { valid: boolean }) => {
    if (isValid)
      if (form.value.privacyPolicies) {
        registerWithEmail()
      } else {
        showPolicyError.value = true
      }
  })
}
onMounted(() => {
  localStorage.removeItem('customer-uuid')
  userType.value = route.query.userType as string
  affiliateToken.value = route.query.via as string
  const email = route.query.email as string
  if (
    userType.value === 'collaborator' &&
    email &&
    validator.isEmail(email, {})
  ) {
    form.value.email = email
    form.value.registeringAsCollaborator = true
  }
  // router.replace({ query: {} })
})

const onNoOfSitesInput = (e: any) => {
  const value = e.target.value
  if (
    value &&
    !isNaN(Number(value)) &&
    Number.isInteger(Number(value)) &&
    Number(value) > 0
  ) {
    form.value.noOfSites = parseInt(value)
  } else {
    form.value.noOfSites = 0
  }
}

const cacheBuster = Math.floor(new Date().getTime())
const apiBaseUrl = import.meta.env.VITE_API_URL

const wlDetails = reactive({
  logo: '',
  name: '',
  logo_height: 60,
  logo_width: 300,
  signup_heading: '',
  signup_subheading: '',
})
const fetchWLDetails = async () => {
  try {
    loading.value = true
    rootStore.showFullscreenLoader = true
    const currentURL = window.location.origin
    const domainWithoutProtocol = currentURL.replace(/^https?:\/\//, '')
    const headers = new Headers({
      'Content-Type': 'application/json',
      pragma: 'no-cache',
      'cache-control': 'no-cache',
      'x-wl-portal-domain': domainWithoutProtocol,
    })
    const response = await fetch(
      `${apiBaseUrl}/white-label/details/${domainWithoutProtocol}?cache=${cacheBuster}&by=portal_domain`,
      { method: 'GET', headers: headers, cache: 'no-store' },
    )
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }
    const data = await response.json()
    if (!data || !data.data) {
      throw new Error('Invalid response data')
    }
    document.title = data.data.name
    wlDetails.logo = data.data.logo_url
    wlDetails.name = data.data.name
    wlDetails.logo_height = data.data.logo_height
    wlDetails.logo_width = data.data.logo_width
    wlDetails.signup_heading = data.data.signup_heading
    wlDetails.signup_subheading = data.data.signup_subheading
  } catch (error) {
    console.error('Error fetching API:', error, window)
  } finally {
    loading.value = false
    rootStore.showFullscreenLoader = false
  }
}

onBeforeMount(async () => {
  // const currentURL = window.location.origin
  // const domainWithoutProtocol = currentURL.replace(/^https?:\/\//, '')
  // if (domainWithoutProtocol === 'my.bionicwp.com') {
  //   rootStore.showFullscreenLoader = true
  // await router.replace({
  //   name: 'all',
  //   query: {
  //     backTo: 'login',
  //     backToPageName: 'Login',
  //   },
  // })
  //   window.location.replace(
  //     'https://launch.bionicwp.com/sick-of-losing-wordpress-clients-for-dumb-reasons/',
  //   )
  //   return
  // }
  await fetchWLDetails()
})

const isMobile = () => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  ) {
    return true
  } else {
    return false
  }
}

const logoSize = computed(() => {
  return (
    getWhiteLabelPortalLogoSize() || {
      height: wlDetails.logo_height,
      width: wlDetails.logo_width,
    }
  )
})
</script>

<template>
  <!-- 👉 Auth wrapper -->
  <VRow>
    <VCol
      class="auth-wrapper d-flex align-center justify-center pa-4"
      :lg="isMobile() ? 12 : 6"
      :md="isMobile() ? 12 : 6"
    >
      <!--      <div class="auth-wrapper d-flex align-center justify-center pa-4">-->
      <!--    <VRow class="position-relative my-sm-16">-->
      <!-- 👉 Auth card -->
      <VCard
        variant="flat"
        class="auth-card pa-4"
        max-width="600"
        :loading="loading"
        border="0"
      >
        <VCardItem class="justify-center">
          <template #prepend>
            <div
              class="d-flex align-center justify-center mb-2"
              :style="{
                maxWidth: `${logoSize.width}px`,
                maxHeight: `${logoSize.height}px`,
              }"
            >
              <img
                :src="getWhiteLabelPortalLogo() || wlDetails.logo"
                alt="logo"
                class="h-100 w-100"
              />
            </div>
          </template>
        </VCardItem>

        <VCardText class="pt-2">
          <h5 class="text-h6 font-weight-semibold mb-1">
            {{ getWhiteLabelSignupHeading() || wlDetails.signup_heading }}
          </h5>
          <p class="mb-0 text-body-1">
            {{ getWhiteLabelSignupSubHeading() || wlDetails.signup_subheading }}
          </p>
        </VCardText>
        <VCardText>
          <VForm ref="refVForm" @submit.prevent="onSubmit">
            <VRow>
              <VCol cols="12">
                <VTextField
                  :id="ElementIDs.AUTH.Register.EMAIL_INPUT"
                  v-model="form.email"
                  bg-color="none"
                  :rounded="0"
                  :rules="[requiredValidator, emailValidator]"
                  :disabled="form.registeringAsCollaborator"
                  :append-inner-icon="
                    form.registeringAsCollaborator ? 'tabler-lock' : ''
                  "
                  label="Email"
                  type="email"
                />
              </VCol>

              <!-- first name -->
              <VCol cols="12">
                <VTextField
                  :id="ElementIDs.AUTH.Register.FIRST_NAME_INPUT"
                  v-model="form.name"
                  bg-color="none"
                  :rounded="0"
                  :rules="[requiredValidator]"
                  label="Name"
                  type="text"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <VTextField
                  :id="ElementIDs.AUTH.Register.PASSWORD_INPUT"
                  v-model="form.password"
                  bg-color="none"
                  label="Password"
                  :rounded="0"
                  :rules="[requiredValidator, strongPasswordValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="
                    isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'
                  "
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>
              <!-- no of sites -->
              <VCol cols="12">
                <VTextField
                  :id="ElementIDs.AUTH.Register.NO_OF_SITES_INPUT"
                  v-model="form.noOfSites"
                  bg-color="none"
                  :rounded="0"
                  type="number"
                  label="How many WordPress sites do you currently manage?"
                  :rules="[requiredValidator, numberValidator]"
                  min="0"
                  @input="onNoOfSitesInput"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>
              <VCol class="pb-0" cols="12">
                <VSelect
                  :id="ElementIDs.AUTH.Register.SIGNUP_SOURCE_INPUT"
                  v-model="form.signupSource"
                  bg-color="none"
                  :rounded="0"
                  label="How did you hear about us?"
                  :items="[
                    'Organic Search',
                    'Facebook Ad',
                    'Referral',
                    'Other',
                  ]"
                />
              </VCol>
              <!-- password -->
              <VCol class="pt2 pb-0" cols="12">
                <div class="mb-4">
                  <div class="d-flex align-center mt-2">
                    <VCheckbox
                      :id="ElementIDs.AUTH.Register.PRIVACY_POLICY_CHECKBOX"
                      v-model="form.privacyPolicies"
                      inline
                    />
                    <VLabel
                      v-if="isBionicSite()"
                      for="privacy-policy"
                      style="opacity: 1"
                    >
                      <span class="me-1">I agree to</span>
                      <a
                        href="https://www.bionicwp.com/terms-of-service/"
                        class="text-on-primary privacy-policy-text font-weight-medium"
                        >privacy policy & terms</a
                      >
                    </VLabel>
                    <VLabel v-else for="privacy-policy" style="opacity: 1">
                      <span class="me-1"
                        >I agree to privacy policy & terms</span
                      >
                    </VLabel>
                    <br />
                  </div>
                  <small
                    v-if="showPolicyError"
                    :id="ElementIDs.AUTH.Register.PRIVACY_POLICY_CHECKBOX_ERROR"
                    class="text-error"
                    >Please check this checkbox to continue</small
                  >
                </div>
              </VCol>
              <VCol v-if="isBionicPlatform" cols="12" class="mt-0 pt-0">
                <div class="recaptcha-container">
                  <div
                    ref="recaptcha"
                    class="g-recaptcha"
                    :data-sitekey="captchaSiteKey"
                  ></div>
                  <p v-if="captchaError" class="text-error">
                    {{ captchaError }}
                  </p>
                </div>
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <VBtn
                  :id="ElementIDs.AUTH.Register.REGISTER_BUTTON"
                  block
                  type="submit"
                  :disabled="loading"
                  :loading="loading"
                  color="main-primary"
                  variant="flat"
                  rounded="0"
                >
                  <span
                    class="font-weight-semibold"
                    :style="{
                      color: `rgb(var(--v-theme-on-main-primary))`,
                    }"
                    >Start Your Free Account Now</span
                  >
                  <template #loader>
                    <VProgressCircular
                      indeterminate
                      color="on-main-primary"
                      size="24"
                    />
                  </template>
                </VBtn>
              </VCol>
              <!-- auth providers -->
              <VCol v-if="isBionicSite()" cols="12" class="text-center">
                <AuthProvider />
              </VCol>

              <!-- login instead -->
              <VCol cols="12" class="text-center text-base">
                <span class="text-on-background" style="opacity: 0.6"
                  >Already have an account?</span
                >
                <RouterLink
                  :id="ElementIDs.AUTH.Register.SIGN_IN_INSTEAD_LINK"
                  class="text-on-background font-weight-medium ms-2"
                  :to="{ name: 'login' }"
                >
                  Sign in instead
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
    <!--    dashboard design -->
    <VCol
      v-if="!isMobile()"
      class="background-color pa-0 ma-0"
      cols="12"
      lg="6"
      md="6"
    >
      <VCol
        cols="12"
        class="d-flex align-center justify-start overflow-hidden dashboard-image-wrapper"
      >
        <img :src="dashboardV2" alt="dashboard" class="dashboard-image" />
      </VCol>
      <VRow class="d-flex justify-center align-center flex-row padding-8">
        <VCol cols="3">
          <Rating
            rating-number="4.8"
            name="Google"
            :rating="[1, 2, 3, 4, 5]"
            :icon="googleIcon"
          />
        </VCol>
        <VCol cols="4" class="d-flex justify-center">
          <Rating
            rating-number="4.8"
            name="Trust Pilot"
            :rating="[1, 2, 3, 4, 5]"
            :icon="ratingColored"
          />
        </VCol>
        <VCol cols="3" class="d-flex justify-start">
          <Rating
            rating-number="4.8"
            name="G2G"
            :rating="[1, 2, 3, 4, 5]"
            :icon="g2g"
          />
        </VCol>
      </VRow>
    </VCol>
  </VRow>
  <!--    </div>-->
  <!--  </div>-->
</template>

<style lang="scss">
@use '@core/scss/template/pages/page-auth.scss';

.privacy-policy-text {
  &:hover {
    text-decoration: underline;
  }
}
.dashboard-image-wrapper {
  //transform: scale(1.1);
  position: relative;
  height: 75vh;
  width: 100%;
  overflow: hidden;
  margin-top: 4.25rem;
}
.dashboard-image {
  position: absolute;
  top: 0;
  width: 100%;
}
.background-color {
  background: #e0f1ff;
  @media (max-width: 800px) {
    display: none;
  }
}
.padding-8 {
  padding-top: 8rem;
  //padding-left: 8rem;
  //padding-right: 8rem;
}
</style>

<!-- @formatter:off-->
<route lang="yaml">
meta:
  layout: blank
  action: read
  subject: Auth
  redirectIfLoggedIn: true
  title: Signup
</route>
